import { useNavigate } from 'react-router-dom';


function HelpPOS() {
    const navigate = useNavigate();

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Navigation Header */}
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/settings')}
                    className="p-2 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all group shadow-sm"
                >
                    <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">POS Keyboard Integration</h1>
                    <p className="text-sm font-bold text-slate-400">Global shortcuts & operational constraints masterclass</p>
                </div>
            </div>

            <div className="space-y-8">
                {/* Core Modifiers Grid */}
                <section className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 p-8 sm:p-10">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center text-2xl">⌨️</div>
                        <h2 className="text-lg font-black text-slate-800 uppercase tracking-widest">Global POS Shortcuts</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            { key: 'Enter', color: 'indigo', title: 'Row Allocation / Search', desc: 'Instantly appends an empty line item underneath your current items (Order mode) or triggers the active search field (Purchase mode).', icon: '⏎' },
                            { key: 'F2', color: 'blue', title: 'Focus Item Lookup', desc: 'Hooks your cursor directly into the first available Product Search Dropdown without reaching for your mouse.', icon: '🔍' },
                            { key: 'F4', color: 'amber', title: 'Focus Status Engine', desc: 'Snaps your cursor right to the active Order/Stock Status selector, letting you toggle states on the fly.', icon: '⚡' },
                            { key: 'F9', color: 'emerald', title: 'Submit Registry', desc: 'Securely triggers form submission and saves the active draft globally across any POS application frame.', icon: '💾' },
                            { key: 'Esc', color: 'rose', title: 'Abort Operation', desc: 'Safely purges the unsaved active draft you are working on and returns your screen to the central directory.', icon: '⎋' }
                        ].map((binding, idx) => (
                            <div key={idx} className="p-6 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-white hover:border-indigo-100 hover:shadow-xl transition-all duration-300 group">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`px-4 py-2 bg-${binding.color}-100 text-${binding.color}-700 rounded-xl font-black font-mono shadow-inner group-hover:scale-110 transition-transform`}>
                                        [{binding.key}]
                                    </div>
                                    <div className={`text-3xl opacity-20 group-hover:opacity-100 transition-opacity text-${binding.color}-500`}>
                                        {binding.icon}
                                    </div>
                                </div>
                                <h3 className={`text-sm font-black uppercase tracking-widest text-${binding.color}-800 mb-2`}>{binding.title}</h3>
                                <p className="text-xs font-bold text-slate-500 leading-relaxed">{binding.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Constraint Modifiers */}
                <section className="bg-slate-900 rounded-3xl shadow-2xl shadow-indigo-200/50 overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 blur-3xl -mr-32 -mt-32 rounded-full group-hover:scale-150 transition-transform duration-1000"></div>
                    <div className="relative z-10 p-8 sm:p-10">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-2xl flex items-center justify-center text-2xl">🛡️</div>
                            <h2 className="text-lg font-black text-rose-100 uppercase tracking-widest">Logic Validation Hooks</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-300 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></span>
                                    Negative Interception
                                </h3>
                                <p className="text-sm font-medium text-slate-300 leading-relaxed">
                                    Our POS system physically prevents formulation of any draft where an item quantity is resolved as <code className="text-rose-400 bg-rose-400/10 px-1 py-0.5 rounded">0</code> or a negative integer. Submissions will intercept natively.
                                </p>
                            </div>
                            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                                    Oversell Constraints
                                </h3>
                                <p className="text-sm font-medium text-slate-300 leading-relaxed">
                                    Non-serialized general items map their volume explicitly through the frontend. If a user dictates a <code className="text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded">quantity &gt; active stock</code>, the flow aborts dynamically.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default HelpPOS;
